import { useCrmLocale } from '../../ui/locale/crmLocale';
import React from 'react';
import { SmartLink } from '../../components/SmartLink';
import { PrefetchLink } from '../../components/PrefetchLink';

/**
 * 403 - Forbidden Page
 * Professional educational business design for access restriction errors
 */
export const Forbidden: React.FC = () => {
  const { t: crmText } = useCrmLocale();

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-gray-50 to-zinc-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-4xl mx-auto text-center">
        {/* Main Error Visual */}
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-linear-to-r from-slate-600/10 via-gray-600/10 to-zinc-600/10 blur-3xl rounded-full" />
          <div className="relative">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-600 rounded-2xl flex items-center justify-center">
              <i className="fas fa-shield-alt text-3xl text-white" />
            </div>
            <h1 className="text-8xl font-black tracking-tight text-transparent bg-clip-text bg-linear-to-r from-slate-600 via-gray-600 to-zinc-600 mb-2">
              403
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 mb-12">
          <h2 className="text-3xl font-bold text-gray-800">{crmText("Access Restricted")}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">{crmText("You don't have permission to view this area of our learning platform. This content may be restricted to specific user roles or require special authorization.")}</p>
          <p className="text-sm text-gray-500">{crmText("If you believe this is an error, please contact an administrator.")}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
          <PrefetchLink
            to="/"
            className="group inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-home group-hover:scale-110 transition-transform" />{crmText("Go Home")}</PrefetchLink>
          
          <PrefetchLink
            to="/employee/login"
            className="group inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-600 font-medium rounded-lg border-2 border-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <i className="fas fa-user-circle group-hover:rotate-12 transition-transform" />{crmText("Switch Account")}</PrefetchLink>
        </div>

        {/* Access Information */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{crmText("Understanding Access Levels")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-user-graduate text-green-600" />
              </div>
              <h4 className="font-medium text-gray-800 mb-1">{crmText("Student Access")}</h4>
              <p className="text-gray-600">{crmText("Courses, exams, and personal dashboard")}</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="fas fa-chalkboard-teacher text-blue-600" />
              </div>
              <h4 className="font-medium text-gray-800 mb-1">{crmText("Instructor Access")}</h4>
              <p className="text-gray-600">{crmText("Class management and grading tools")}</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="fas fa-users-cog text-purple-600" />
              </div>
              <h4 className="font-medium text-gray-800 mb-1">{crmText("Administrative")}</h4>
              <p className="text-gray-600">{crmText("System administration and settings")}</p>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 mb-2">{crmText("Need access to this area?")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm">
            <SmartLink to="mailto:admin@arabiancademy.com" className="text-gray-600 hover:text-gray-800 hover:underline transition-colors">{crmText("Contact Administrator")}</SmartLink>
            <span className="hidden sm:block text-gray-300">|</span>
            <SmartLink to="mailto:support@arabiancademy.com" className="text-gray-600 hover:text-gray-800 hover:underline transition-colors">{crmText("Technical Support")}</SmartLink>
          </div>
        </div>

        {/* Error Code */}
        <div className="mt-8 text-xs uppercase tracking-widest text-gray-400">{crmText("Error Code: FORBIDDEN")}</div>
      </div>
    </div>
  );
};

export default Forbidden;